import React from "react";
import {
  ShoppingCart, Package, Building, Car, Factory, Layers, Plane, MapPin,
} from "lucide-react";
import { useT } from "@/lib/i18n";
import LogisticsAnimation from "@/components/LogisticsAnimation";

const IMG_PLANE = "https://images.unsplash.com/photo-1721592873149-9823d9dc6b40?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwyfHxjYXJnbyUyMGFpcmNyYWZ0JTIwcnVud2F5JTIwbmlnaHR8ZW58MHx8fHwxNzgwMDEwODg3fDA&ixlib=rb-4.1.0&q=85";
const IMG_SHIP = "https://images.pexels.com/photos/33315751/pexels-photo-33315751.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const IMG_WH = "https://images.unsplash.com/photo-1721937127582-ed331de95a04?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBsb2dpc3RpY3MlMjB3YXJlaG91c2UlMjBkYXJrfGVufDB8fHx8MTc4MDAxMDg4N3ww&ixlib=rb-4.1.0&q=85";

export default function IndustriesSection() {
  const { t } = useT();
  const INDUSTRIES = [
    { icon: MapPin, name: t("ind.turkey.t"), desc: t("ind.turkey.d"), img: IMG_SHIP, premium: true, accent: "turkey" },
    { icon: ShoppingCart, name: t("ind.ecom.t"), desc: t("ind.ecom.d"), img: IMG_PLANE },
    { icon: Package, name: t("ind.fba.t"), desc: t("ind.fba.d"), img: IMG_WH },
    { icon: Building, name: t("ind.fairs.t"), desc: t("ind.fairs.d"), img: IMG_SHIP },
    { icon: Car, name: t("ind.auto.t"), desc: t("ind.auto.d"), img: IMG_PLANE, premium: true },
    { icon: Plane, name: t("ind.aero.t"), desc: t("ind.aero.d"), img: IMG_PLANE, premium: true },
    { icon: Factory, name: t("ind.industrial.t"), desc: t("ind.industrial.d"), img: IMG_SHIP },
    { icon: Layers, name: t("ind.courier.t"), desc: t("ind.courier.d"), img: IMG_WH },
  ];

  return (
    <section data-testid="industries-section" className="relative py-24 sm:py-28 overflow-hidden">
      <LogisticsAnimation variant="all" />
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="max-w-2xl mb-14">
          <div className="zp-label">{t("ind.eyebrow")}</div>
          <h2 className="font-display text-3xl sm:text-5xl tracking-tighter mt-3">
            {t("ind.title.a")}{" "}
            <span className="text-[color:var(--zp-text-3)]">{t("ind.title.b")}</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {INDUSTRIES.map((it, i) => (
            <article
              key={i}
              data-testid={`industry-card-${i}`}
              className="group relative rounded-2xl overflow-hidden border border-[color:var(--zp-border)] bg-white aspect-[4/5] shadow-[0_12px_30px_-20px_rgba(10,37,64,0.14)] hover:shadow-[0_20px_40px_-20px_rgba(46,198,204,0.35)] transition-shadow"
            >
              <img
                src={it.img}
                alt={it.name}
                className="absolute inset-0 w-full h-full object-cover opacity-20 group-hover:opacity-35 group-hover:scale-105 transition-all duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-white via-white/92 to-white/75" />
              <div className="absolute inset-0 p-6 flex flex-col justify-end">
                {it.premium && (
                  <span className="self-start text-[10px] uppercase tracking-[0.2em] font-bold text-[color:var(--zp-cyan-3)] bg-[color:var(--zp-cyan-soft)] px-2 py-1 rounded-full mb-3">
                    {it.accent === "turkey" ? "Turkey Lane" : "White-Glove"}
                  </span>
                )}
                <div className="w-11 h-11 rounded-xl bg-[color:var(--zp-navy)] grid place-items-center text-[#5CE1E6] mb-4 shadow-lg">
                  <it.icon size={18} strokeWidth={1.7} />
                </div>
                <h3 className="font-display text-lg tracking-tight">{it.name}</h3>
                <p className="text-[color:var(--zp-text-2)] text-sm mt-2 leading-relaxed">{it.desc}</p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
